import * as React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Cancel';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Switch,
  Button,
  Chip,
  Collapse,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  SnackbarContent,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CircularProgress from '@material-ui/core/CircularProgress';
import styles from './AcceleratedTesters.styles';
import { LoadingCircle, Dropzone, TitleBackButton } from '../common';
import { PERFORMANCE_LEVEL_COLORS } from '../common/performanceLevelColors';
import { resolveStudent, LEVEL_OPTIONS, SUBJECT_LABEL } from './+store/acceleratedTesters.selectors';

// File types the TEA Accelerated Testers listing arrives as (CSV from TEAL, or the
// xls/xlsx sibling export). Matches the Assessment Manager dropzone's accepted set.
const UPLOAD_ACCEPT = ['text/csv', 'application/vnd.ms-excel', '.csv', '.xls', '.xlsx'];

const FILTER_OPTIONS = [
  { value: 'needsAttention', label: 'Needs attention' },
  { value: 'all', label: 'All' },
  { value: 'scored', label: 'Scored' },
  { value: 'assumed', label: 'Assumed (projected)' },
  { value: 'nonParticipant', label: 'Non-participant' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'teaNo', label: 'TEA "No"' },
];

// Score inputs to surface per accelerated subject (SPEC §5.2 table).
const SUBJECT_SCORE_FIELDS = {
  reading: [
    { key: 'satEbrw', label: 'SAT EBRW' },
    { key: 'actEngRead', label: 'ACT Eng+Read' },
  ],
  math: [
    { key: 'satMath', label: 'SAT Math' },
    { key: 'actMath', label: 'ACT Math' },
  ],
  science: [{ key: 'actScience', label: 'ACT Science' }],
};

const emptyDraft = (entry) => {
  const o = (entry && entry.override) || {};
  return {
    satEbrw: o.satEbrw != null ? String(o.satEbrw) : '',
    satMath: o.satMath != null ? String(o.satMath) : '',
    actEngRead: o.actEngRead != null ? String(o.actEngRead) : '',
    actMath: o.actMath != null ? String(o.actMath) : '',
    actScience: o.actScience != null ? String(o.actScience) : '',
    assumedLevel: o.assumedLevel || '',
    excluded: o.excluded === true,
    note: o.note || '',
  };
};

class AcceleratedTesters extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    profile: PropTypes.object,
    snapshotYear: PropTypes.number,
    fileYear: PropTypes.number,
    init: PropTypes.func.isRequired,
    getAcceleratedTesters: PropTypes.func.isRequired,
    updateOverride: PropTypes.func.isRequired,
    updateOverridesBulk: PropTypes.func.isRequired,
    uploadFile: PropTypes.func.isRequired,
    acceleratedTesters: PropTypes.object,
    schoolList: PropTypes.object,
    loading: PropTypes.bool,
    loadingMsg: PropTypes.string,
    upload: PropTypes.object,
  };

  state = {
    isLoadedBefore: false,
    editing: false,
    search: '',
    filter: 'needsAttention',
    campusFilter: 'all',
    expandedId: null,
    draft: {},
    bulkLevel: 'meets',
    bulkDialogOpen: false,
    toast: null,
    lastUploadSeq: 0,
    filterInitialized: false,
  };

  componentDidMount() {
    // The upload redux slice persists across navigation, so a previously-completed
    // upload still carries a non-zero seq + result. Baseline lastUploadSeq to the
    // current store seq on mount so we only toast for uploads done THIS visit, not a
    // stale one every time the page loads.
    const { upload } = this.props;
    this.setState({ lastUploadSeq: (upload && upload.seq) || 0 });
    this.shouldInit();
  }

  componentDidUpdate() {
    this.shouldInit();
    this.handleUploadResult();
    this.maybeInitFilter();
  }

  // The default filter is the "Needs attention" worklist, but once everyone is
  // resolved that view is empty and the page looks blank. On first data load, if
  // nobody needs attention, start on "All" instead so the resolved students show.
  maybeInitFilter = () => {
    const { acceleratedTesters } = this.props;
    if (this.state.filterInitialized) return;
    if (!acceleratedTesters || Object.keys(acceleratedTesters).length === 0) return;
    const counts = this.computeCounts();
    this.setState({ filterInitialized: true, filter: counts.needsAttention > 0 ? 'needsAttention' : 'all' });
  };

  // Fire a toast whenever the upload lifecycle settles (seq bumps once per finished
  // upload, success or error). Driven by redux so it survives the node refresh.
  handleUploadResult = () => {
    const { upload } = this.props;
    if (!upload || upload.seq === this.state.lastUploadSeq) return;
    const next = { lastUploadSeq: upload.seq };
    if (upload.error) {
      this.setState(Object.assign(next, { toast: { isError: true, text: upload.error } }));
    } else if (upload.result) {
      const fy = upload.result.fileYear ? `${upload.result.fileYear} ` : '';
      this.setState(
        Object.assign(next, {
          toast: {
            isError: false,
            text: `${fy}Accelerated Testers file imported and matched to the roster. Cached reports were cleared.`,
          },
        })
      );
    } else {
      this.setState(next);
    }
  };

  handleDrop = (accepted) => {
    const { uploadFile, profile, snapshotYear, upload } = this.props;
    if (upload && upload.uploading) return;
    if (accepted && accepted.length) {
      uploadFile(profile, accepted[0], snapshotYear);
    }
  };

  shouldInit = () => {
    const { profile, init, getAcceleratedTesters, snapshotYear } = this.props;
    const { isLoadedBefore } = this.state;
    if (profile && !isLoadedBefore) {
      this.setState({ isLoadedBefore: true });
      init(profile);
      getAcceleratedTesters(snapshotYear);
    }
  };

  handleEditStateChange = () => {
    this.setState((prev) => ({ editing: !prev.editing, expandedId: null }));
  };

  handleSearchChange = (event) => this.setState({ search: event.target.value });
  handleFilterChange = (event) => this.setState({ filter: event.target.value });
  handleCampusChange = (event) => this.setState({ campusFilter: event.target.value });
  handleBulkLevelChange = (event) => this.setState({ bulkLevel: event.target.value });

  toggleRow = (firebaseId, entry) => {
    if (!this.state.editing) return;
    this.setState((prev) =>
      prev.expandedId === firebaseId
        ? { expandedId: null, draft: {} }
        : { expandedId: firebaseId, draft: emptyDraft(entry) }
    );
  };

  handleDraftChange = (key, value) => {
    this.setState((prev) => ({ draft: Object.assign({}, prev.draft, { [key]: value }) }));
  };

  buildOverride = (draft) => {
    const override = {};
    ['satEbrw', 'satMath', 'actEngRead', 'actMath', 'actScience'].forEach((key) => {
      const trimmed = (draft[key] || '').trim();
      override[key] = trimmed === '' ? null : Number(trimmed);
    });
    override.assumedLevel = draft.assumedLevel || null;
    override.excluded = draft.excluded === true;
    override.note = (draft.note || '').trim();
    return override;
  };

  handleSave = (firebaseId, entry) => {
    const { updateOverride, snapshotYear } = this.props;
    const { draft } = this.state;
    const override = this.buildOverride(draft);
    updateOverride(snapshotYear, firebaseId, override);
    // Resolve the new label optimistically for the toast.
    const merged = Object.assign({}, entry, {
      override: Object.assign({}, entry && entry.override, override),
    });
    const resolved = resolveStudent(merged, this.resolveYear());
    this.setState({
      expandedId: null,
      draft: {},
      toast: {
        isError: false,
        text: `Saved. Resolved level: ${resolved.label}. Cached reports were cleared.`,
      },
    });
  };

  handleCancel = () => this.setState({ expandedId: null, draft: {} });

  openBulkDialog = () => this.setState({ bulkDialogOpen: true });
  closeBulkDialog = () => this.setState({ bulkDialogOpen: false });

  getUnresolvedIds = () => {
    const { acceleratedTesters, snapshotYear } = this.props;
    return Object.entries(acceleratedTesters || {})
      .filter(([, entry]) => {
        const resolved = resolveStudent(entry, this.resolveYear());
        return resolved.classification !== 'scored' && resolved.classification !== 'assumed';
      })
      .map(([firebaseId]) => firebaseId);
  };

  handleBulkApply = () => {
    const { updateOverridesBulk, snapshotYear } = this.props;
    const { bulkLevel } = this.state;
    const ids = this.getUnresolvedIds();
    const updates = {};
    ids.forEach((firebaseId) => {
      updates[firebaseId] = { assumedLevel: bulkLevel };
    });
    updateOverridesBulk(snapshotYear, updates);
    this.setState({
      bulkDialogOpen: false,
      toast: {
        isError: false,
        text: `Assumed level applied to ${ids.length} unresolved students. Cached reports were cleared.`,
      },
    });
  };

  closeToast = () => this.setState({ toast: null });

  // The cut-point table is keyed by ACCOUNTABILITY year (snapshotYear + 1), matching
  // the backend builder's getCutPointsForYear(snapshotYear + 1). Prefer the node's
  // fileYear; fall back to snapshotYear + 1.
  resolveYear = () => {
    const { fileYear, snapshotYear } = this.props;
    return fileYear || Number(snapshotYear) + 1;
  };

  getCampusName = (campusNumber) => {
    const { schoolList } = this.props;
    if (schoolList && campusNumber && schoolList[campusNumber]) {
      return schoolList[campusNumber].name;
    }
    return campusNumber || '';
  };

  getCampusOptions = () => {
    const { acceleratedTesters } = this.props;
    const campuses = {};
    Object.values(acceleratedTesters || {}).forEach((entry) => {
      const campus = entry.teaCampusNumber || entry.rosterCampusNumber;
      if (campus) campuses[campus] = this.getCampusName(campus);
    });
    return Object.entries(campuses).map(([value, label]) => ({ value, label }));
  };

  // Header tallies across the whole node.
  computeCounts = () => {
    const { acceleratedTesters, snapshotYear } = this.props;
    const counts = {
      scored: 0,
      assumed: { doesNotMeet: 0, approaches: 0, meets: 0, masters: 0, total: 0 },
      needsAttention: 0,
      teaNo: 0,
    };
    Object.values(acceleratedTesters || {}).forEach((entry) => {
      const resolved = resolveStudent(entry, this.resolveYear());
      if (resolved.classification === 'scored') counts.scored += 1;
      if (resolved.classification === 'assumed') {
        counts.assumed.total += 1;
        // One level per student (the rolled-up best assumed level), so the per-level
        // breakdown sums to total even when a student has multiple assumed subjects.
        if (resolved.ammCode && counts.assumed[resolved.ammCode] != null) {
          counts.assumed[resolved.ammCode] += 1;
        }
      }
      if (resolved.needsAttention) counts.needsAttention += 1;
      if (entry.teaSatOrAct === false) counts.teaNo += 1;
    });
    return counts;
  };

  matchesFilter = (entry, resolved) => {
    const { filter } = this.state;
    switch (filter) {
      case 'all':
        return true;
      case 'needsAttention':
        return resolved.needsAttention;
      case 'scored':
        return resolved.classification === 'scored';
      case 'assumed':
        return resolved.classification === 'assumed';
      case 'nonParticipant':
        return resolved.classification === 'nonParticipant';
      case 'excluded':
        return resolved.classification === 'excluded';
      case 'teaNo':
        return entry.teaSatOrAct === false;
      default:
        return true;
    }
  };

  matchesSearch = (entry) => {
    const { search } = this.state;
    if (!search) return true;
    const needle = search.trim().toLowerCase();
    const haystack = [entry.firstName, entry.lastName, entry.localId, entry.studentId, entry.tsdsId]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  };

  getVisibleRows = () => {
    const { acceleratedTesters, snapshotYear } = this.props;
    const { campusFilter } = this.state;
    const rows = Object.entries(acceleratedTesters || {})
      .map(([firebaseId, entry]) => ({ firebaseId, entry, resolved: resolveStudent(entry, this.resolveYear()) }))
      .filter(({ entry, resolved }) => {
        const campus = entry.teaCampusNumber || entry.rosterCampusNumber;
        if (campusFilter !== 'all' && campus !== campusFilter) return false;
        return this.matchesFilter(entry, resolved) && this.matchesSearch(entry);
      });
    // Default sort: unresolved (no real/assumed score) first — the worklist.
    rows.sort((a, b) => {
      const aResolved = a.resolved.hasScore || a.resolved.classification === 'assumed' ? 1 : 0;
      const bResolved = b.resolved.hasScore || b.resolved.classification === 'assumed' ? 1 : 0;
      if (aResolved !== bResolved) return aResolved - bResolved;
      const aName = `${a.entry.lastName || ''} ${a.entry.firstName || ''}`;
      const bName = `${b.entry.lastName || ''} ${b.entry.firstName || ''}`;
      return aName.localeCompare(bName);
    });
    return rows;
  };

  renderSubjects = (entry) => {
    const subjects = (entry && entry.subjects) || {};
    return Object.keys(subjects)
      .filter((k) => subjects[k] != null)
      .map((k) => SUBJECT_LABEL[k] || k)
      .join(', ');
  };

  renderLevelCell = (resolved) => {
    const { classes } = this.props;
    if (resolved.classification === 'dataGap') {
      // Same state the header tallies as "Needs attention" (TEA expects a result, we
      // have no score yet) — keep label + color consistent across both places.
      return <Chip label="Needs attention" className={classNames(classes.countChip, classes.needsAttentionChip)} />;
    }
    if (resolved.classification === 'nonParticipant') {
      return <Chip label="Non-participant" className={classes.countChip} />;
    }
    if (resolved.classification === 'excluded') {
      return <Chip label="Excluded" className={classes.countChip} />;
    }
    // Scored or assumed: color the chip by performance level using the shared STAAR
    // palette so Meets/Masters/etc. match Progress Goals and the rest of the app.
    const bg = PERFORMANCE_LEVEL_COLORS[resolved.ammCode];
    if (bg) {
      return (
        <Chip
          label={resolved.label}
          className={classNames(classes.countChip, classes.levelChip)}
          style={{ backgroundColor: bg }}
        />
      );
    }
    return <span>{resolved.label}</span>;
  };

  renderEditor = (firebaseId, entry, resolved) => {
    const { classes } = this.props;
    const { draft } = this.state;
    const subjects = (entry && entry.subjects) || {};
    const subjectKeys = Object.keys(subjects).filter((k) => subjects[k] != null);
    // The assumed select is inert when a real score already resolves the student.
    const realScoreResolves = resolved.hasScore;

    return (
      <div className={classes.editorGrid}>
        <Typography variant="subtitle2">Score inputs (per accelerated subject)</Typography>
        <div className={classes.editorRow}>
          {subjectKeys.map((subject) =>
            (SUBJECT_SCORE_FIELDS[subject] || []).map((field) => (
              <TextField
                key={field.key}
                className={classes.scoreField}
                label={field.label}
                value={draft[field.key] || ''}
                onChange={(e) => this.handleDraftChange(field.key, e.target.value)}
                type="number"
                disabled={draft.excluded}
              />
            ))
          )}
        </div>

        <div className={classes.editorRow}>
          <FormControl className={classes.assumedSelect}>
            <InputLabel>Assumed level</InputLabel>
            <Select
              value={draft.assumedLevel || ''}
              onChange={(e) => this.handleDraftChange('assumedLevel', e.target.value)}
              disabled={realScoreResolves || draft.excluded}
              displayEmpty
            >
              <MenuItem value="">—</MenuItem>
              {LEVEL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.excluded}
                onChange={(e) => this.handleDraftChange('excluded', e.target.checked)}
              />
            }
            label="Exclude from accountability"
          />
        </div>

        <TextField
          className={classes.noteField}
          label="Note"
          value={draft.note || ''}
          onChange={(e) => this.handleDraftChange('note', e.target.value)}
          multiline
        />

        <Typography className={classes.helpText}>
          A real SAT/ACT score always wins over an assumed level. An ACT override competes with the ingested SAT score
          (best result wins); an SAT override replaces the ingested SAT score. Exclude removes the student from
          accountability entirely.
        </Typography>

        <div className={classes.editorActions}>
          <Button onClick={this.handleCancel}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={() => this.handleSave(firebaseId, entry)}>
            Save
          </Button>
        </div>
      </div>
    );
  };

  renderUploadZone = () => {
    const { classes, upload } = this.props;
    const busy = !!(upload && upload.uploading);
    return (
      <Dropzone
        accept={UPLOAD_ACCEPT}
        multiple={false}
        disabled={busy}
        onDrop={this.handleDrop}
        className={classNames(classes.uploadZone, busy ? classes.uploadZoneBusy : null)}
        activeClassName={classes.uploadZoneActive}
      >
        {busy ? (
          <React.Fragment>
            <CircularProgress size={26} className={classes.uploadIcon} />
            <Typography className={classes.uploadTitle}>Uploading and Processing ...</Typography>
            <Typography className={classes.uploadHint}>
              Parsing the file, matching students to the roster, and clearing cached reports.
            </Typography>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <CloudUploadIcon className={classes.uploadIcon} />
            <Typography className={classes.uploadTitle}>Import a TEA Accelerated Testers file</Typography>
            <Typography className={classes.uploadHint}>
              Upload your &quot;Accelerated_Testers_Final_&lt;year&gt;_&lt;district&gt;.csv&quot; export here.
            </Typography>
          </React.Fragment>
        )}
      </Dropzone>
    );
  };

  renderToast = () => {
    const { classes } = this.props;
    const { toast } = this.state;
    if (!toast) return null;
    const Icon = toast.isError ? ErrorIcon : CheckCircleIcon;
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open
        autoHideDuration={5000}
        onClose={this.closeToast}
      >
        <SnackbarContent
          className={classes[toast.isError ? 'error' : 'success']}
          message={
            <span className={classes.message}>
              <Icon className={classes.icon} />
              {toast.text}
            </span>
          }
          action={[
            <IconButton key="close" aria-label="Close" color="inherit" onClick={this.closeToast}>
              <CloseIcon className={classes.icon} />
            </IconButton>,
          ]}
        />
      </Snackbar>
    );
  };

  render() {
    const { classes, profile, loading, loadingMsg, acceleratedTesters, fileYear } = this.props;
    const { editing, search, filter, campusFilter, expandedId, bulkLevel, bulkDialogOpen } = this.state;

    if (!profile || loading) {
      return (
        <div className={classes.content}>
          <LoadingCircle classes={classes} msg={loadingMsg} />
        </div>
      );
    }

    const isEmpty = !acceleratedTesters || Object.keys(acceleratedTesters).length === 0;
    const counts = this.computeCounts();
    const rows = this.getVisibleRows();
    const campusOptions = this.getCampusOptions();
    const unresolvedCount = this.getUnresolvedIds().length;
    const bulkLevelLabel = (LEVEL_OPTIONS.find((o) => o.value === bulkLevel) || {}).label || bulkLevel;

    return (
      <div className={classes.root}>
        {this.renderToast()}

        <TitleBackButton title="Accelerated Testers" />

        <div className={classes.headerRow}>
          <div className={classes.countChips}>
            <Chip className={classes.countChip} color="primary" label={`Scored: ${counts.scored}`} />
            <Chip
              className={classes.countChip}
              label={`Assumed: ${counts.assumed.total} (M ${counts.assumed.masters} / Mt ${counts.assumed.meets} / A ${counts.assumed.approaches} / DNM ${counts.assumed.doesNotMeet})`}
            />
            <Chip
              className={classNames(classes.countChip, classes.needsAttentionChip)}
              label={`Needs attention: ${counts.needsAttention}`}
            />
            <Chip className={classes.countChip} label={`TEA "No": ${counts.teaNo}`} />
          </div>
          <div className={classes.editSwitchWrap}>
            <Typography variant="body2">Edit</Typography>
            <Switch checked={editing} onChange={this.handleEditStateChange} color="primary" />
          </div>
        </div>

        {fileYear && (
          <Typography className={classes.helpText}>
            {`${fileYear} TEA Accelerated Testers list. Each student's level comes from the SAT and ACT score files you upload; for every subject the higher of the two counts (science is ACT-only). Use the manual overrides below to enter a score we don't have, project an assumed level, or exclude a student.`}
          </Typography>
        )}

        {this.renderUploadZone()}

        {editing && (
          <div className={classes.bulkBar}>
            <Typography variant="body2">Assume level</Typography>
            <FormControl className={classes.bulkSelect}>
              <Select value={bulkLevel} onChange={this.handleBulkLevelChange}>
                {LEVEL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2">{`for all ${unresolvedCount} unresolved`}</Typography>
            <Button
              variant="contained"
              color="primary"
              style={{ marginLeft: 12 }}
              disabled={unresolvedCount === 0}
              onClick={this.openBulkDialog}
            >
              Apply
            </Button>
          </div>
        )}

        <div className={classes.controlsRow}>
          <TextField
            className={classes.searchField}
            label="Search name or ID"
            value={search}
            onChange={this.handleSearchChange}
          />
          <FormControl className={classes.filterSelect}>
            <InputLabel>Filter</InputLabel>
            <Select value={filter} onChange={this.handleFilterChange}>
              {FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className={classes.campusSelect}>
            <InputLabel>Campus</InputLabel>
            <Select value={campusFilter} onChange={this.handleCampusChange}>
              <MenuItem value="all">All campuses</MenuItem>
              {campusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {isEmpty && (
          <div>
            No Accelerated Testers file has been imported for this year yet. Drop the TEA file in the box above to
            import it (or upload it from the Assessment Manager — either path works).
          </div>
        )}

        {!isEmpty && rows.length === 0 && (
          <div className={classes.emptyFilterMsg}>
            <Typography>
              {`No students match the "${(FILTER_OPTIONS.find((o) => o.value === filter) || {}).label ||
                filter}" filter`}
              {campusFilter !== 'all' ? ' for this campus' : ''}.
            </Typography>
            <Button color="primary" onClick={() => this.setState({ filter: 'all', campusFilter: 'all' })}>
              Show all students
            </Button>
          </div>
        )}

        {!isEmpty && rows.length > 0 && (
          <div className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Local ID</TableCell>
                  <TableCell>Campus</TableCell>
                  <TableCell>Accelerated subject(s)</TableCell>
                  <TableCell>TEA SAT/ACT</TableCell>
                  <TableCell>Resolved level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(({ firebaseId, entry, resolved }) => {
                  const campus = entry.teaCampusNumber || entry.rosterCampusNumber;
                  const isExpanded = expandedId === firebaseId;
                  return (
                    <React.Fragment key={firebaseId}>
                      <TableRow
                        hover={editing}
                        className={editing ? classes.clickableRow : null}
                        onClick={() => this.toggleRow(firebaseId, entry)}
                      >
                        <TableCell>{`${entry.lastName || ''}, ${entry.firstName || ''}`}</TableCell>
                        <TableCell>{entry.localId}</TableCell>
                        <TableCell>{this.getCampusName(campus)}</TableCell>
                        <TableCell>{this.renderSubjects(entry)}</TableCell>
                        <TableCell>{entry.teaSatOrAct === false ? 'No' : 'Yes'}</TableCell>
                        <TableCell>{this.renderLevelCell(resolved)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} style={{ padding: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <div className={classes.expandedCell}>
                              {isExpanded && this.renderEditor(firebaseId, entry, resolved)}
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <Dialog open={bulkDialogOpen} onClose={this.closeBulkDialog}>
          <DialogTitle>Apply assumed level?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`This will set an assumed performance level of "${bulkLevelLabel}" for all ${unresolvedCount} currently
              unresolved students. Assumed levels are projections, not real scores — they count toward accountability
              the same way but are flagged as projected, and any real SAT/ACT score arriving later automatically
              supersedes the assumption. You can flip exceptions one student at a time afterward.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeBulkDialog}>Cancel</Button>
            <Button onClick={this.handleBulkApply} variant="contained" color="primary">
              Apply to {unresolvedCount}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(AcceleratedTesters);
